import { DEFAULT_RTP } from '@/constants/DefaultRTP';
import { GameSchema } from './types';

export const getGameAttributes = (game: GameSchema) => {
  return ({
    id: game.id,
    game_name: game.game_name,
    release_date: game.release_date,
    rtp: game.rtp,
    volatility: game.volatility,
    min_bet: game.min_bet,
    max_bet: game.max_bet,
    buy_feature: game.buy_feature,
    status: game.status,
  } = game);
};

type GetFirstGameByRtpOptions = {
  gameStatus?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

const parseReleaseDateMs = (value: string | undefined | null) => {
  if (!value) return undefined;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? undefined : ms;
};

export const getFirstGameByRTP = (
  games: GameSchema[],
  gameStatus?: GetFirstGameByRtpOptions['gameStatus'],
  sortBy?: GetFirstGameByRtpOptions['sortBy'],
  sortOrderInput?: GetFirstGameByRtpOptions['sortOrder'],
): GameSchema | undefined => {
  if (!games || games.length === 0) {
    return undefined;
  }
  const sortByReleaseDate = sortBy === 'release_date';
  const sortByRtp = sortBy === 'rtp';
  const sortOrder: 'asc' | 'desc' =
    sortOrderInput === 'asc' ? 'asc' : 'desc';

  const statusScoped =
    gameStatus && gameStatus.length > 0
      ? games.filter((g) =>
          gameStatus.includes(g?.status?.toLowerCase() || ''),
        )
      : games;

  // When the list is ranked by RTP, each card has to show the variant it is
  // ranked by: the highest RTP when sorting max -> min, the lowest when
  // sorting min -> max.
  if (sortByRtp && sortOrderInput) {
    const candidates = (
      statusScoped.length > 0 ? statusScoped : games
    ).filter((g) => g.rtp != null);

    if (candidates.length > 0) {
      return candidates.reduce((extreme, current) =>
        sortOrder === 'asc'
          ? current.rtp < extreme.rtp
            ? current
            : extreme
          : current.rtp > extreme.rtp
            ? current
            : extreme,
      );
    }
  }

  const shouldScopeToStatusAndSort =
    !!sortByReleaseDate &&
    !!sortOrderInput &&
    !!gameStatus &&
    gameStatus.length > 0;

  const scopedGames = shouldScopeToStatusAndSort
    ? statusScoped
        .slice()
        .sort((a: GameSchema, b: GameSchema) => {
          const direction = sortOrder === 'asc' ? 1 : -1;

          if (sortBy === 'release_date') {
            const aMs = parseReleaseDateMs(a.release_date);
            const bMs = parseReleaseDateMs(b.release_date);
            if (aMs == null && bMs == null) return 0;
            if (aMs == null) return 1;
            if (bMs == null) return -1;
            return (aMs - bMs) * direction;
          }

          return 0;
        })
        .filter(
          (g, _i, arr) => g.release_date === arr[0].release_date,
        )
    : games;

  const findDefaultRtpGame = (list: GameSchema[]) => {
    return list.reduce((closest, current) => {
      const currentDiff = Math.abs(current.rtp - DEFAULT_RTP);
      const closestDiff = Math.abs(closest.rtp - DEFAULT_RTP);
      return currentDiff < closestDiff ? current : closest;
    }, list[0]);
  };

  const defaultRtpInScope = findDefaultRtpGame(scopedGames);
  if (defaultRtpInScope) {
    return defaultRtpInScope;
  } else {
    return findDefaultRtpGame(games);
  }
};
